/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { Modal } from '@components/molecules'
import {
	useAppSelector,
	useAppDispatch,
	shipmentActions,
	configActions,
} from '@redux/index'
import { DispatchType } from '@schemas'
import { mono, boShared, boMedia } from '@components/systems/backoffice/backoffice.tokens'
import dayjs from 'dayjs'

/**
 * 추적 갱신·라벨 결과 등 한 줄짜리 알림을 전역 토스트로 넘긴다. 그리는 것은 없다.
 *
 * 라벨 만들기 화면이 서 있는 동안은 넘기지 않는다 — 그 메시지는 폼 옆에 남아야 읽힌다
 * (`ShipmentLabelCreate` 가 같은 `shipment.message` 를 인라인으로 보여 준다).
 */
export const ShipmentMessageModal = () => {
	const reduxDispatch = useAppDispatch()
	const message: string = useAppSelector(state => state.shipment.message)
	const labelFormOpen: boolean = useAppSelector(state => state.shipment.labelFormOpen)

	React.useEffect(() => {
		if (message === '' || labelFormOpen) return

		reduxDispatch(configActions.TOAST({ isShow: true, message: message }))
		reduxDispatch(shipmentActions.MESSAGE(''))
	}, [message, labelFormOpen])

	return null
}

/**
 * FedEx 라벨 하나의 스캔 이력 (`/main/shipping` 행 클릭).
 * 목록의 행은 배송 건(박스)이고 라벨은 `shipmentId` 로 찾는다 — 라벨 없는 건은 열지 않는다.
 */
export const ShipmentScanModal = () => {
	const reduxDispatch = useAppDispatch()
	const shipmentId: string = useAppSelector(state => state.shipment.scanModalShipmentId)
	const list: DispatchType[] = useAppSelector(state => state.shipment.shipmentList)
	const shipment =
		shipmentId === '' ? undefined : list.find(s => s.shipmentId === shipmentId)

	const onClose = () => reduxDispatch(shipmentActions.SCAN_MODAL_SHIPMENT_ID(''))

	return (
		<Modal
			show={shipment !== undefined}
			target="shipmentScan"
			title={shipment !== undefined ? `송장 ${shipment.trackingNumber}` : '배송'}
			showSubmit={false}
			closeTitle="닫기"
			onCloseModal={onClose}
		>
			{shipment !== undefined && (
				<View css={style}>
					<View className="boSpecRow">
						<span className="boSpecLabel">주문</span>
						<span className="boSpecValue">
							{shipment.orderId || shipment.partyName} · {shipment.receiverName} ·{' '}
							{shipment.countryIso}
						</span>
					</View>
					<View className="boSpecRow">
						<span className="boSpecLabel">최근 상태</span>
						<span className="boSpecValue">
							{shipment.lastStatusDesc || '—'}
							{shipment.lastTrackedAt !== '' &&
								` · ${dayjs(shipment.lastTrackedAt).format('YYYY-MM-DD HH:mm')}`}
						</span>
					</View>
					{shipment.scanEvents.length === 0 ? (
						<Text className="boEmpty">스캔 이력이 아직 없습니다.</Text>
					) : (
						<View className="scanList">
							{shipment.scanEvents.map((e, i) => (
								<View className="scanRow" key={i}>
									<span className="scanDate">
										{e.date !== '' ? dayjs(e.date).format('MM-DD HH:mm') : ''}
									</span>
									<span className="scanDesc">{e.description}</span>
									<span className="scanLoc">{e.location}</span>
								</View>
							))}
						</View>
					)}
					<a
						className="trackLink"
						href={`https://www.fedex.com/fedextrack/?trknbr=${shipment.trackingNumber}`}
						target="_blank"
						rel="noreferrer"
					>
						FedEx 에서 보기
					</a>
				</View>
			)}
		</Modal>
	)
}

const style = css({
	...boShared,
	// 폰 390 에서 420 이 화면 밖으로 나갔다 (2026-09-29)
	minWidth: 'min(420px, 100%)',
	maxWidth: '100%',
	'.scanList': { marginTop: 8, maxHeight: 320, overflowY: 'auto' },
	'.scanRow': {
		display: 'grid',
		gridTemplateColumns: '92px 1fr 140px',
		gap: 12,
		padding: '8px 0',
		borderBottom: `1px solid ${mono.line}`,
		fontSize: 13,
		fontWeight: 300,
	},
	'.scanDate': { color: mono.dim, fontVariantNumeric: 'tabular-nums' },
	'.scanLoc': { color: mono.dim, textAlign: 'right' },
	'.trackLink': {
		display: 'inline-block',
		marginTop: 12,
		fontSize: 12,
		color: mono.dim,
		textDecoration: 'underline',
	},
	[boMedia.phone]: {
		'.scanRow': { gridTemplateColumns: '1fr', gap: 2 },
		'.scanLoc': { textAlign: 'left' },
	},
})
